import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AddressService } from '../../services/address.service';
import { MessageService } from '../../services/message-service';
import { Location } from '../../models/location';
import { NormalizedUsAddress } from '../../models/normalized-us-address';

interface SearchAddress {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
}

export interface InitialAddress {
    streetAddress: string;
    location: Location | null;
}

@Component({
    selector: 'address-normalizer',
    templateUrl: './address-normalizer.component.html',
    styleUrls: ['./address-normalizer.component.scss']
})
export class AddressNormalizerComponent implements OnInit, OnChanges, OnDestroy {
    @Input() initialAddress: InitialAddress = { streetAddress: '', location: null };

    @Output() selectedAddressChange = new EventEmitter<NormalizedUsAddress | null>();

    form = new FormGroup({
        streetAddress: new FormControl<string>('', Validators.required),
        location: new FormControl<Location | null>(null, Validators.required),
        selectedNormalizedAddress: new FormControl<NormalizedUsAddress | null>(null)
    });

    normalizedAddresses: NormalizedUsAddress[] = [];
    loadingNormalization = false;
    searchCompleted = false;
    searchError = false;

    private destroyed = new Subject<void>();
    private searchRequest = new Subject<SearchAddress>();
    private lastSearchedAddress: SearchAddress | null = null;
    private initialized = false;

    constructor(
        private addressService: AddressService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        merge(
            this.form.controls.streetAddress.valueChanges,
            this.form.controls.location.valueChanges
        ).pipe(
            takeUntil(this.destroyed),
            debounceTime(500),
            map(() => ({
                streetAddress: this.form.controls.streetAddress.value,
                location: this.form.controls.location.value
            })),
            tap(({ streetAddress, location }) => {
                const isValid = streetAddress && streetAddress.length >= 5 && !!location?.city;

                if (!isValid && this.normalizedAddresses.length > 0) {
                    this.clearSearchResults();
                }
            }),
            filter(({ streetAddress, location }) =>
                streetAddress && streetAddress.length >= 5 && !!location?.city),
            filter(({ streetAddress, location }) => this.isNewSearch(streetAddress, location))
        ).subscribe(({ streetAddress, location }) => {
            this.searchRequest.next({
                streetAddress,
                city: location.city,
                state: location.state,
                postalCode: location.postalCode
            });
        });

        this.searchRequest.pipe(
            takeUntil(this.destroyed),
            tap(() => this.setLoadingState()),
            switchMap(input => this.lookupNormalizedAddress(input))
        ).subscribe(addresses => this.handleSearchResults(addresses));

        this.form.controls.selectedNormalizedAddress.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(address => {
                this.selectedAddressChange.emit(address);
            });

        this.initialized = true;
        this.resetForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.initialized && changes['initialAddress']) {
            this.resetForm();
        }
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    private normalize(s: string | null | undefined): string {
        return s?.trim().toLowerCase() ?? '';
    }

    private isNewSearch(streetAddress: string, location: Location): boolean {
        const current = {
            streetAddress: this.normalize(streetAddress),
            city: this.normalize(location?.city),
            state: this.normalize(location?.state),
            postalCode: this.normalize(location?.postalCode)
        };

        if (this.lastSearchedAddress
            && current.streetAddress === this.lastSearchedAddress.streetAddress
            && current.city === this.lastSearchedAddress.city
            && current.state === this.lastSearchedAddress.state
            && current.postalCode === this.lastSearchedAddress.postalCode
        ) {
            return false;
        }

        this.lastSearchedAddress = current;
        return true;
    }

    private clearSearchResults(): void {
        this.normalizedAddresses = [];
        this.searchCompleted = false;
        this.searchError = false;
        this.form.controls.selectedNormalizedAddress.reset();
        this.selectedAddressChange.emit(null);
    }

    private setLoadingState(): void {
        this.loadingNormalization = true;
        this.searchCompleted = false;
        this.searchError = false;
        this.normalizedAddresses = [];
        this.form.controls.selectedNormalizedAddress.reset();
        this.selectedAddressChange.emit(null);
    }

    private handleSearchResults(addresses: NormalizedUsAddress[] | null): void {
        this.loadingNormalization = false;

        if (addresses === null) {
            this.searchError = true;
            return;
        }

        this.normalizedAddresses = this.addFallbackIfEmpty(addresses);
        this.searchCompleted = true;
        this.autoSelectMatchingAddress();
    }

    private lookupNormalizedAddress(address: SearchAddress): Observable<NormalizedUsAddress[] | null> {
        const query = `${address.streetAddress}, ${address.city}, ${address.state} ${address.postalCode}`;

        return this.addressService.search(query).pipe(
            catchError(() => {
                return of(null);
            })
        );
    }

    // NOTE(nicholas-ramsey): If the API is unable to normalize, then we'll just accept whatever the user entered.
    // This should rarely occur. It seems to only happen with `Attn: ` lines.
    private createFallbackAddress(): NormalizedUsAddress | null {
        const streetAddress = this.form.controls.streetAddress.value;
        const location = this.form.controls.location.value;

        if (!streetAddress || !location) {
            return null;
        }

        return {
            address1: streetAddress,
            city: location.city,
            state: location.state,
            postalCode: location.postalCode,
            isParsedFromQuery: true,
            county: '',
            extendedPostalCode: ''
        };
    }

    private addFallbackIfEmpty(addresses: NormalizedUsAddress[]): NormalizedUsAddress[] {
        if (addresses.length > 0) {
            return addresses;
        }

        const fallback = this.createFallbackAddress();
        return fallback ? [fallback] : [];
    }

    private resetForm(): void {
        this.normalizedAddresses = [];
        this.loadingNormalization = false;
        this.searchCompleted = false;
        this.searchError = false;
        this.lastSearchedAddress = null;

        const { streetAddress, location } = this.initialAddress;

        this.form.reset({
            streetAddress: streetAddress ?? '',
            location: location,
            selectedNormalizedAddress: null
        }, { emitEvent: false });

        this.selectedAddressChange.emit(null);

        if (streetAddress && location?.city) {
            this.searchRequest.next({
                streetAddress,
                city: location.city,
                state: location.state,
                postalCode: location.postalCode
            });
        }
    }

    /**
     * Selects the first normalized address that matches what the user entered.
     * This is so they don't have to select it themselves, and so that on initial load they don't have to re-select it.
     */
    private autoSelectMatchingAddress(): void {
        if (this.normalizedAddresses.length === 0) {
            return;
        }

        const streetAddress = this.normalize(this.form.controls.streetAddress.value);
        const location = this.form.controls.location.value;
        const city = this.normalize(location?.city);
        const state = this.normalize(location?.state);
        const postalCode = this.normalize(location?.postalCode);

        const match = this.normalizedAddresses.find(addr =>
            this.normalize(addr.address1) === streetAddress &&
            this.normalize(addr.city) === city &&
            this.normalize(addr.state) === state &&
            this.normalize(addr.postalCode) === postalCode
        );

        if (match) {
            this.form.controls.selectedNormalizedAddress.setValue(match);
        }
    }

    selectAddress(address: NormalizedUsAddress): void {
        this.form.controls.selectedNormalizedAddress.setValue(address);
    }
}
